import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';

import {
    ENDPOINT_DATA_GEOSERVICIOS,
    GIS_ALERT_ICON_SIZE_PIXELS,
    GIS_POINT_ICON_SIZE_PIXELS,
    GIS_SOUND_ALERT_MAXIMUM_DURATION_SECONDS,
    PointColors,
    REALTIME_INTERVAL_SECONDS,
} from '../../../constants';
import { invertCoordinates } from '../../../helpers/generalFunctions';
import AlertIcon from '../icons/AlertIcon';
import PointIcon from '../icons/PointIcon';
import PointTooltip from '../tooltips/PointTooltip';

import 'leaflet-realtime';

/**
 * Create point in GIS
 * @param {number} idCliente - Client id for the request.
 * @param {string} colorPoints - Colors for the points
 * @param {string} ptws - tws a mostrar en mapa
 * @param {() => any} playSoundAlert - Function that plays sound alert, a sound alert needs is played when a point meets
 * certain conditions.
 * @param {() => any} stopSoundAlert - Function that stops sound alert, a sound alert needs is stopped when user clicks
 * @param {(pointID:String) => void} senPointId - send point id to parent component
 * a button or after a certain amount of time.
 * @returns Array of two elements: points layergroup and points realtime reference, the reference needs to be returned
 * to stop the subscription later.
 */
const getPointsLayerGroupRealtime = (idCliente, colorPoints, ptws, playSoundAlert, stopSoundAlert, senPointId) => {

    const pointsLayerGroup = new L.LayerGroup();
    const realtimeReference = L.realtime(
        {
            url: ENDPOINT_DATA_GEOSERVICIOS,
            method: 'POST',
            body: JSON.stringify({
                solicitud: 'Puntos',
                idCliente: idCliente,
                ptws: ptws,
            }),
        },
        {
            container: pointsLayerGroup,
            interval: REALTIME_INTERVAL_SECONDS * 1000,
            onEachFeature: (feature, layer) => {
                layer.bindTooltip(ReactDOMServer.renderToString(<PointTooltip {...feature.properties} />), {
                    direction: 'top',
                });
            },
            pointToLayer: (feature, latlng) => {
                const marker = L.marker(latlng, {
                    icon: createPointIcon(feature, colorPoints),
                    pmIgnore: true,
                });

                marker.on('click', () => {
                    senPointId(feature.properties.id);
                });

                return marker;
            },
            removeMissing: true,
            updateFeature: (feature, oldLayer, newLayer) => {
                if (
                    feature.properties.nivel !== 0 &&
                    (!oldLayer || feature.properties.nivel !== oldLayer.feature.properties.nivel)
                ) {
                    const pointMarker = L.marker(invertCoordinates(feature.geometry.coordinates), {
                        icon: createAlertIcon(),
                        pmIgnore: true,
                    });
                    if (!oldLayer) {
                        /*
                        Leaves 5 seconds for user to interact with the application.
                        
                        This is because if the user hasn't interacted with the application,
                        the browser won't allow sounds to be played.
                        */
                        setTimeout(() => {
                            playSoundAlert();
                        }, [5000]);
                    } else {
                        playSoundAlert();
                    }
                    setTimeout(() => {
                        pointMarker.setIcon(createPointIcon(feature, colorPoints));
                        stopSoundAlert();
                    }, [GIS_SOUND_ALERT_MAXIMUM_DURATION_SECONDS * 1000]);
                    return pointMarker;
                }
                return newLayer;
            },
        }
    );
    return [pointsLayerGroup, realtimeReference];
};

/**
 * @param {*} feature - GeoJSON feature, the point varies depending on the feature properties.
 * @param {string} colorPoints - Colors for the points
 * @returns Point icon.
 */
const createPointIcon = (feature, colorPoints) => {
    return L.divIcon({
        className: '',
        html: ReactDOMServer.renderToString(createPointHtml(feature, colorPoints)),
        iconSize: [GIS_POINT_ICON_SIZE_PIXELS, GIS_POINT_ICON_SIZE_PIXELS],
        iconAnchor: [GIS_POINT_ICON_SIZE_PIXELS / 2, GIS_POINT_ICON_SIZE_PIXELS],
        tooltipAnchor: [0, -GIS_POINT_ICON_SIZE_PIXELS],
    });
};

/**
 * @param {*} feature - GeoJSON feature, the point varies depending on the feature properties.
 * @param {string} colorPoints - Colors for the points
 * @returns Point React component, ready to be converted to HTML.
 */
const createPointHtml = (feature, colorPoints) => {
    const colorP = colorPoints.split(',')


    if (feature.properties.nivel === 3) {
        return (
            <PointIcon
                fillColor={colorP[3]}
                fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
                strokeColor={PointColors[3].stroke}
            />
        );
    } else if (feature.properties.nivel === 2) {
        return (
            <PointIcon
                fillColor={colorP[2]}
                fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
                strokeColor={PointColors[2].stroke}
            />
        );
    } else if (feature.properties.nivel === 1) {
        return (
            <PointIcon
                fillColor={colorP[1]}
                fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
                strokeColor={PointColors[1].stroke}
            />
        );
    } else if (feature.properties.nivel === 0) {
        return (
            <PointIcon
                fillColor={colorP[0]}
                fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
                strokeColor={PointColors[0].stroke}
            />
        );
    } else {
        return <PointIcon fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`} />;
    }
};

/**
 * @returns Alert icon.
 */
const createAlertIcon = () => {
    return L.divIcon({
        className: '',
        html: ReactDOMServer.renderToString(<AlertIcon fontSize={`${GIS_ALERT_ICON_SIZE_PIXELS}px`} />),
        iconSize: [GIS_ALERT_ICON_SIZE_PIXELS, GIS_ALERT_ICON_SIZE_PIXELS],
    });
};

export { getPointsLayerGroupRealtime };
